<template>
    <DModal
        :title="__('customers.contracts.create_contract_tree.product')"
        is-confirm
        :show="showModal"
        :disabled-action="unableToLoadOptions"
        is-medium
        @confirm="submitForm"
        @close="$emit('close-modal')"
    >
        <DCard
            padding
            class="product-form"
        >
            <ProductSelection
                :exclude-modules="['ff_package']"
                :multiple="false"
                :selected-product-group="selectedProductGroup"
                :selected-products="selectedProduct"
                :required="true"
                :retired="form.product_retired"
                :market-region-ids="customer.market_region_ids || []"
                @product-group-update="handleGroupChange"
                @update="updateProduct"
                @reset-product-id="resetProductId"
            />
            <p
                v-if="errors.product_id"
                class="error-msg"
            >
                {{ errors.product_id }}
            </p>

            <DAlert
                v-if="form.up_and_downgrade_only === true"
                color="warning"
            >
                {{ __('customers.contracts.up_and_downgrade_only.warning') }}
                <DFormRow
                    label=""
                    :error="errors.up_and_downgrade_only_confirmed"
                >
                    <DFormCheck
                        id="up_and_downgrade_only_confirmed"
                        v-model="form.up_and_downgrade_only_confirmed"
                        :label="__('customers.contracts.up_and_downgrade_only.checkbox_hint')"
                    />
                </DFormRow>
            </DAlert>

            <DFormRow
                :label="__('customers.contracts.product_plan')"
                class="pt-3"
                :error="errors.product_plan_id"
            >
                <DSelect
                    id="product_plan"
                    v-model="form.product_plan_id"
                    :options="productPlans"
                    :placeholder="__('customers.contracts.select_product_plan')"
                    :disabled="!form.product_id"
                    @change="getPriceInfoFromPlan()"
                />
            </DFormRow>

            <div v-if="quantityExist">
                <DFormRow
                    v-for="(row, index) in form.config_values"
                    :key="index"
                    :label="__('customers.contracts.create_contract_tree.quantity')"
                    :is-required="true"
                    :error="['numeric'].includes(row.type) && ['quantity'].includes(row.name) ? errors.quantity : ''"
                >
                    <DInput
                        v-if="['numeric'].includes(row.type) && ['quantity'].includes(row.name)"
                        id="quantity"
                        v-model="form.config_values[index].value"
                    />
                </DFormRow>
            </div>

            <hr>

            <StartDate
                v-model="form.start_date"
                :contract="contract"
                :errors="errors"
                :required="true"
            />

            <Notes
                v-model:notesInternal="form.notes_internal"
                v-model:notesInvoice="form.notes_invoice"
                :errors="errors"
            />
        </DCard>
    </DModal>
    <DModal
        :show="unableToLoadOptions === true"
        :title="__('contracts.modals.options_not_loaded.title')"
        :message="__('contracts.modals.options_not_loaded.description')"
        is-error
    />
</template>

<script>

import StartDate from '~@/Components/Customers/Contracts/FormFields/StartDate'
import Notes from '~@/Components/Customers/Contracts/FormFields/Notes'
import ProductSelection from '~@/Components/Products/Products/ProductSelection'
export default {
    name: 'ProductModal',
    components: { ProductSelection, Notes, StartDate },
    props: {
        contract: {
            type: Object,
            default: () => { return {} }
        },
        customer: {
            type: Object,
            default: () => { return {} }
        },
        showModal: {
            type: Boolean,
            default: false
        },
        errors: {
            type: Object,
            default: () => { return {} }
        }
    },
    emits: ['close-modal', 'submitProduct', 'oneTimePlanSelected'],
    data () {
        return {
            form: JSON.parse(JSON.stringify(this.contract)), // clone object
            productPlans: this.contract.available_product_plans,
            quantityExist: false,
            unableToLoadOptions: false
        }
    },
    computed: {
        selectedProductGroup () {
            if (this.contract.product_group_id) {
                return Number(this.contract.product_group_id)
            }

            return 0
        },
        selectedProduct () {
            if (this.contract.product_id) {
                return [{
                    value: this.contract.product_id,
                    label: `${this.contract.product_name}${this.contract.product_retired ? ' (' + this.__('products.products.index.retired') + ')' : ''}`
                }]
            }

            return []
        }
    },
    created () {
        this.checkIfQuantityExist()
    },
    methods: {
        submitForm () {
            this.$emit('submitProduct', this.form)
        },
        handleGroupChange (value) {
            this.form.product_group_id = value
        },
        updateProduct (value) {
            const valueChanged = Number(this.form.product_id) !== Number(value.value)
            this.form.product_id = value.value
            this.form.product_name = value.label
            this.form.product_retired = value.retired
            this.form.fulfillment_module = value.fulfillment_module
            this.form.up_and_downgrade_only = value.up_and_downgrade_only === true
            this.form.up_and_downgrade_only_confirmed = false

            this.isSelectedPlanOneTime = false

            this.$emit('oneTimePlanSelected', this.form, this.isSelectedPlanOneTime)

            if (Object.hasOwn(value, 'lead_time')) {
                const today = new Date()
                this.form.start_date = new Date(today.setDate(today.getDate() + value.lead_time)).toISOString().substring(0, 10)
            }
            const plans = []
            for (const plan of value.plans) {
                plans.push(plan)
            }

            this.form.available_product_plans = [{ label: '', value: '' }, ...plans]
            this.productPlans = plans

            // Reset ProductPlans on Product change.
            if (valueChanged) {
                this.form.product_plan_id = null
            }

            // If there is only one plan and product plan is not already set, use it as default
            if (this.form.product_plan_id === null && plans.length === 1) {
                this.form.product_plan_id = plans[0].value
                this.getPriceInfoFromPlan()
            }

            this.updateContractConfiguration()
        },
        async updateContractConfiguration () {
            this.unableToLoadOptions = false

            // Empty the available options
            this.form.config_values = []

            if (this.form.product_id) {
                const response = await window.axios.get(this.$route('customers.customers.contracts.options', {
                    customer: this.customer.id,
                    product: this.form.product_id
                }))

                if (response.data.options === null) {
                    this.unableToLoadOptions = true
                    return
                }

                for (const option of response.data.options) {
                    this.form.config_values.push(option)
                }

                this.checkIfQuantityExist()
            }
        },
        getPriceInfoFromPlan () {
            for (const planObject of this.productPlans) {
                if (parseInt(planObject.value) === parseInt(this.form.product_plan_id)) {
                    this.isSelectedPlanOneTime = planObject.one_time

                    this.$emit('oneTimePlanSelected', this.form, this.isSelectedPlanOneTime)

                    this.form.price = planObject.price.price
                    this.form.billing_cycle = planObject.price.billing_cycle
                    this.form.setup_fee = planObject.price.setup_fee
                    this.form.contract_term = planObject.price.contract_term
                    this.form.contract_term_unit = planObject.price.contract_term_unit
                    this.form.notice_period = planObject.price.notice_period
                    this.form.notice_period_unit = planObject.price.notice_period_unit
                    this.form.new_price_price = null
                    this.form.new_price_months = null
                    this.form.new_price_months_with_discount = null
                    this.form.new_price_without_discount = null
                    this.form.new_price_discount_name = null
                    this.form.new_price_discount_value = null
                    this.form.new_price_discount_type = null
                    this.form.new_price_with_discount = null
                    this.form.valid_from_month_required = true

                    let firstPrice = null
                    let secondPrice = null

                    for (const price of planObject.prices) {
                        if (firstPrice === null) {
                            firstPrice = price

                            continue
                        }

                        secondPrice = price

                        break
                    }

                    // Logic for taking discount and new prices can be founded in OCN-7583
                    // If we have just one price take only discount
                    if (planObject.prices.length === 1 ||
                        (firstPrice.price === secondPrice?.price && firstPrice.discount_value === null)
                    ) {
                        this.form.new_price_discount_name = planObject.price.discount_name
                        this.form.new_price_discount_value = planObject.price.discount_value
                        this.form.new_price_discount_type = planObject.price.discount_type
                        this.form.valid_from_month_required = false

                        break
                    }

                    if (firstPrice !== null && secondPrice !== null) {
                        if (firstPrice.price !== secondPrice.price) {
                            this.form.new_price_price = String(secondPrice.price)
                            this.form.new_price_months = String(secondPrice.valid_from_month - firstPrice.valid_from_month)
                            this.form.new_price_without_discount = true

                            break
                        }

                        if (firstPrice.price === secondPrice.price) {
                            this.form.new_price_discount_name = firstPrice.discount_name
                            this.form.new_price_discount_value = firstPrice.discount_value
                            this.form.new_price_discount_type = firstPrice.discount_type
                            this.form.new_price_months_with_discount = String(secondPrice.valid_from_month - firstPrice.valid_from_month)
                            if (firstPrice.discount_value === secondPrice.discount_value && firstPrice.discount_type === secondPrice.discount_type) {
                                this.form.valid_from_month_required = false
                                this.form.new_price_months_with_discount = null
                            }
                        }
                    }
                }
            }
        },
        checkIfQuantityExist () {
            for (const config of this.form.config_values) {
                if (config.name === 'quantity') {
                    this.quantityExist = true

                    return
                }
            }

            this.quantityExist = false
        },
        resetProductId () {
            this.form.product_group_id = this.contract.product_group_id
            this.form.product_id = this.contract.product_id
            this.form.product_plan_id = this.contract.product_plan_id
            this.form.price = null
            this.form.product_name = null
            this.form.fulfillment_module = null
            this.form.up_and_downgrade_only = false
            this.form.up_and_downgrade_only_confirmed = false
        }
    }
}
</script>

<style lang="scss" scoped>
.product-form {
    display: grid;
    grid-gap: 15px;
    border-radius: 0 !important;
    border-bottom: 0 !important;
}
html body div#app.app div.d-main-wrapper.c-wrapper main.d-main div div.d-modal section.modal-box.medium main div.d-card.product-form main.padding div.product-selection {
    grid-gap: 0;
}
.error-msg {
    margin: 3px 20px 0 0;
    text-align: center;
    font-size: 12px;
    color: var(--colorDanger);
}
</style>
